import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  Link,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AttendanceDay, AbsenceReasonSubmitRequest } from '../types/employeePortal';
import { employeePortalAPI } from '../utils/api';

interface AbsenceReasonModalProps {
  open: boolean;
  onClose: () => void;
  day: AttendanceDay | null;
  onSubmitSuccess: () => void;
}

export const ABSENCE_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
export const ABSENCE_ATTACHMENT_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
export const validateAbsenceAttachment = (file: File): 'tooLarge' | 'invalidType' | null => {
  if (file.size > ABSENCE_ATTACHMENT_MAX_BYTES) return 'tooLarge';
  if (!ABSENCE_ATTACHMENT_TYPES.includes(file.type)) return 'invalidType';
  return null;
};

const AbsenceReasonModal: React.FC<AbsenceReasonModalProps> = ({ open, onClose, day, onSubmitSuccess }) => {
  const { t } = useTranslation('auth');
  const { t: tc } = useTranslation('common');
  const [reasonType, setReasonType] = useState<AbsenceReasonSubmitRequest['reason_type']>('sick_leave');
  const [customReason, setCustomReason] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  useEffect(() => {
    if (open) {
      setError(null);
      setDeleteConfirm(false);
      if (!day?.absence_reason) {
        setReasonType('sick_leave');
        setCustomReason('');
        setAttachment(null);
      }
    }
  }, [open, day]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size (5MB)
    if (validateAbsenceAttachment(file) === 'tooLarge') {
      setError(t('employeePortal.fileTooLarge'));
      return;
    }

    // Validate type
    if (validateAbsenceAttachment(file) === 'invalidType') {
      setError(t('employeePortal.invalidFileType'));
      return;
    }

    setError(null);
    setAttachment(file);
  };

  const handleSubmit = async () => {
    if (!day) return;

    if (reasonType === 'custom' && !customReason.trim()) {
      setError(t('employeePortal.customReasonRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('date', day.date);
      formData.append('reason_type', reasonType);
      if (reasonType === 'custom') {
        formData.append('custom_reason', customReason);
      }
      if (attachment) {
        formData.append('attachment', attachment);
      }

      const response = await employeePortalAPI.submitAbsenceReason(formData);
      if (response.success) {
        onSubmitSuccess();
      } else {
        setError(response.message || t('employeePortal.unexpectedError'));
      }
    } catch (err: any) {
      const backendError = err.response?.data?.message || err.response?.data?.errors;
      setError(typeof backendError === 'string' ? backendError : t('employeePortal.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!day?.absence_reason?.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await employeePortalAPI.deleteAbsenceReason(day.absence_reason.id);
      if (response.success) {
        onSubmitSuccess();
      } else {
        setError(response.message || t('employeePortal.unexpectedError'));
      }
    } catch (err: any) {
      setError(t('employeePortal.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  const isViewMode = !!day?.absence_reason;
  const employeeAccent = {
    main: 'var(--an-primary)',
    dark: 'var(--an-primary-dark)',
    soft: 'rgb(var(--an-primary-rgb) / 0.04)',
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 'var(--an-radius-lg)',
          border: '1px solid var(--an-border-light)',
          boxShadow: 'var(--an-shadow-lg)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--an-border-light)', bgcolor: 'var(--an-surface)' }}>
        <Typography variant="h6" component="div">
          {t('employeePortal.absenceReasonFor', { date: day?.date })}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <i className="bi bi-x-lg"></i>
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: 'var(--an-surface)', borderColor: 'var(--an-border-light)' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isViewMode ? (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('employeePortal.reasonType.label')}
              </Typography>
              <Typography variant="body1">
                {t(`employeePortal.reasonType.${day?.absence_reason?.reason_type}`)}
              </Typography>
            </Box>

            {day?.absence_reason?.reason_type === 'custom' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('employeePortal.customReason')}
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {day?.absence_reason?.custom_reason}
                </Typography>
              </Box>
            )}

            {day?.absence_reason?.attachment_url && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('employeePortal.attachment')}
                </Typography>
                <Button
                  component={Link}
                  href={day.absence_reason.attachment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<i className="bi bi-eye"></i>}
                  sx={{ 
                    textTransform: 'none',
                    borderColor: employeeAccent.main,
                    color: employeeAccent.main,
                    '&:hover': {
                      borderColor: employeeAccent.dark,
                      bgcolor: employeeAccent.soft
                    }
                  }}
                >
                  {t('employeePortal.viewAttachment')}
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {deleteConfirm ? (
              <Box sx={{ bgcolor: 'error.lighter', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
                <Typography variant="body2" color="error.main" sx={{ mb: 2, fontWeight: 'medium' }}>
                  {t('employeePortal.deleteAbsenceReasonConfirm')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={handleDelete}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <i className="bi bi-trash"></i>}
                  >
                    {t('employeePortal.deleteAbsenceReason')}
                  </Button>
                  <Button variant="outlined" size="small" onClick={() => setDeleteConfirm(false)} disabled={loading}>
                    {tc('buttons.cancel')}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Button
                color="error"
                startIcon={<i className="bi bi-trash"></i>}
                onClick={() => setDeleteConfirm(true)}
                disabled={loading}
              >
                {t('employeePortal.deleteAbsenceReason')}
              </Button>
            )}
          </Box>
        ) : (
          <Box component="form" sx={{ mt: 1 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="reason-type-label">{t('employeePortal.reasonType.label')}</InputLabel>
              <Select
                labelId="reason-type-label"
                value={reasonType}
                label={t('employeePortal.reasonType.label')}
                onChange={(e) => setReasonType(e.target.value as any)}
                disabled={loading}
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: employeeAccent.main,
                  },
                }}
              >
                <MenuItem value="sick_leave">{t('employeePortal.reasonType.sick_leave')}</MenuItem>
                <MenuItem value="out_of_office">{t('employeePortal.reasonType.out_of_office')}</MenuItem>
                <MenuItem value="emergency_leave">{t('employeePortal.reasonType.emergency_leave')}</MenuItem>
                <MenuItem value="custom">{t('employeePortal.reasonType.custom')}</MenuItem>
              </Select>
            </FormControl>

            {reasonType === 'custom' && (
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('employeePortal.customReason')}
                placeholder={t('employeePortal.customReasonPlaceholder')}
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                    borderColor: employeeAccent.main,
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: employeeAccent.main,
                  },
                }}
                required
                disabled={loading}
                inputProps={{ maxLength: 500 }}
                helperText={`${customReason.length}/500`}
              />
            )}

            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('employeePortal.attachmentOptional')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<i className="bi bi-upload"></i>}
                  disabled={loading}
                  sx={{ borderColor: employeeAccent.main, color: employeeAccent.main, '&:hover': { borderColor: employeeAccent.main, bgcolor: employeeAccent.soft } }}
                >
                  {t('employeePortal.chooseFile')}
                  <input type="file" hidden accept=".jpg,.jpeg,.png,.pdf" onChange={handleFileChange} />
                </Button>
                {attachment ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {attachment.name}
                    </Typography>
                    <IconButton size="small" onClick={() => setAttachment(null)} color="error">
                      <i className="bi bi-x-lg"></i>
                    </IconButton>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t('employeePortal.noFileChosen')}
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('employeePortal.allowedFileTypes')}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: 'var(--an-surface-muted)', borderTop: '1px solid var(--an-border-light)' }}>
        <Button onClick={onClose} disabled={loading}>
          {tc('buttons.cancel')}
        </Button>
        {!isViewMode && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{ bgcolor: employeeAccent.main, '&:hover': { bgcolor: employeeAccent.dark } }}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {loading ? t('employeePortal.submitting') : t('employeePortal.submitAbsenceReason')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AbsenceReasonModal;

